import _                                         from 'underscore';
import React, { useCallback, useMemo, useState } from 'react';

interface FilterSummaryItem {
	filter: any,
	value: any
}

export interface UseFilteringHook {
	
	isFilterSetDefaultSet( defaultFilters: object, activeFilters: object | null ): boolean,
	
	isFilterSetSubsetOfDefaultSet( defaultFilters: object, activeFilters: object | null ): boolean,
	
	isFilterValueRanged( value: any | any[] ): boolean,
	
	getFilterValueObjects( defaultFilters: object, activeFilters: object | null, excludeFilters?: string[] ): FilterSummaryItem[],
	
	removeAllUnsupportedItems( defaultFilters: object, activeFilters: object | null ): any,
	
	getFilterSubset<D>( filterSet: D, omitFiltersHash: { [P in keyof Partial<D>]: boolean } ): any,
}

export const useFiltering = (): UseFilteringHook => {
	
	const isFilterSetDefaultSet: UseFilteringHook['isFilterSetDefaultSet'] = ( defaultFilters, activeFilters ) => {
		
		return _.isEqual( defaultFilters, activeFilters );
	}
	
	const isFilterSetSubsetOfDefaultSet: UseFilteringHook['isFilterSetSubsetOfDefaultSet'] = ( defaultFilters, activeFilters ) => {
		
		const sharedKeys = _.intersection( _.keys( activeFilters ), _.keys( defaultFilters ) );
		
		return _.isEqual(
			_.pick( defaultFilters, sharedKeys ),
			_.pick( activeFilters, sharedKeys )
		);
	}
	
	const isFilterValueRanged: UseFilteringHook['isFilterValueRanged'] = ( value: any | any[], valuesCanBeInclusive = false ) => {
		
		return _.isArray( value )
			&& ( value.length === 2 )
			&& (
				( !valuesCanBeInclusive && value[ 0 ] !== value[ 1 ] ) ||   // either the values should not match
				( valuesCanBeInclusive === true )                           // or it's ok that we evaluate if they do or not
			)
	}
	
	const getFilterValueObjects: UseFilteringHook['getFilterValueObjects'] = ( defaultFilters = {}, activeFilters = {}, excludeFilters = [] ) => {
		
		let availableFilters   = _.omit( defaultFilters, excludeFilters ),
		    filterValueObjects = [] as FilterSummaryItem[];
		
		_.mapObject( availableFilters, ( value, filterKey ) => {
			
			if( !_.isEqual(
				{ f: activeFilters[ filterKey ] },
				{ f: availableFilters[ filterKey ] } )
			){
				if( !_.isUndefined( activeFilters[ filterKey ] ) ){
					filterValueObjects.push( {
						filter: filterKey,
						value:  activeFilters[ filterKey ]
					} );
				}
			}
			
		} );
		
		return filterValueObjects;
	}
	
	const removeAllUnsupportedItems: UseFilteringHook['removeAllUnsupportedItems'] = ( defaultFilters = {}, activeFilters = {} ) => {
		
		if( !activeFilters ) return activeFilters;
		
		let defaultKeys         = _.keys( defaultFilters ),
		    activeKeys          = _.keys( activeFilters ),
		    sharedKeys          = _.intersection( defaultKeys, activeKeys ),
		    supportedFilterKeys = _.union( defaultKeys, sharedKeys ),
		    newActiveFilters    = {};
		
		supportedFilterKeys.map( key => {
			if( activeFilters[ key ] ){
				newActiveFilters[ key ] = activeFilters[ key ]
			}
		} );
		
		return newActiveFilters;
	}
	
	function getFilterSubset<D>( filterSet, filterAttributeOmitHash ): UseFilteringHook['getFilterSubset']{
		
		let omissionSet = _.compact( _.keys( filterAttributeOmitHash ).map( attribute => {
			
			return ( filterAttributeOmitHash[ attribute ] === true ) ? attribute : null;
			
		} ) );
		
		return _.omit( filterSet, omissionSet )
	}
	
	return {
		isFilterSetDefaultSet,
		isFilterSetSubsetOfDefaultSet,
		isFilterValueRanged,
		getFilterValueObjects,
		getFilterSubset,
		removeAllUnsupportedItems,
	}
};
